import { createAsyncThunk } from '@reduxjs/toolkit';
import capitalize from 'lodash/capitalize';
import Relay from 'v2/services/relay';
import { fetchData } from 'services/helpers';
import status from 'store/reducers/common/constants';

const relay = new Relay('project');

export const PROJECTS_ROWS_PER_PAGE = 20;

// Fetches one page. The offset comes back on the payload so the reducer can
// line the rows up with the page the table is showing.
const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async ({
    term = '',
    page = 0,
    limit = PROJECTS_ROWS_PER_PAGE,
    order = '',
    desc = 1,
  } = {}) => {
    const offset = page * limit;
    const params = { limit, offset };
    if (term) {
      params.query = term;
    }
    if (order) {
      params.order = order;
      params.desc = desc;
    }
    return relay
      .get('', params)
      .then((result) => result.json())
      .then((result) => ({ ...result, offset }));
  }
);

const fetchStatusList = createAsyncThunk('projects/fetchStatusList', async () =>
  fetchData('project/constants').then((result) => result.data.project.status)
);

const changeStatus = createAsyncThunk(
  'projects/changeStatus',
  async ({ project, status: statusProject }) => {
    const { id } = project;
    const { id: idStatus } = statusProject;
    return relay.patch({ data: { status: idStatus } }, id);
  }
);

export default {
  [fetchProjects.pending]: (state, { meta }) => {
    state.status = {
      severity: 'info',
      message: 'Loading ',
      type: status.LOADING_STATUS,
    };
    state.listRequestId = meta.requestId;
  },
  [fetchProjects.fulfilled]: (state, { payload, meta }) => {
    // Page requests are not cancelled, so ignore a superseded one.
    if (state.listRequestId !== meta.requestId) {
      return;
    }
    state.status = {
      severity: false,
      message: 'IDLE ',
      type: status.IDLE_STATUS,
    };
    const list =
      payload && payload.data && payload.data.length ? payload.data : [];
    const offset = (payload && payload.offset) || 0;
    // TODO: Check unit-no column
    // TODO: Check approx-cost column
    const projects = list.map((project) => ({
      ...project,
      project: project.name,
      location: project.region,
      'unit-no': 'n/a',
      'created-by':
        project.author_name && project.author_name !== ''
          ? project.author_name
          : 'No User Data',
      'approx-cost': 'n/a',
      'published-date': project.created_at,
    }));
    // Pad the pages we have not fetched so this one sits at the right index.
    const placeholders = [...new Array(offset).keys()].map((index) => ({
      id: `mock-id-${index}`,
      placeholder: true,
    }));
    state.list = [...placeholders, ...projects];
    state.listCount = (payload && payload.info && payload.info.count) || 0;
    state.listOffset = offset;
  },
  [fetchProjects.rejected]: (state, { meta }) => {
    if (state.listRequestId !== meta.requestId) {
      return;
    }
    state.status = {
      severity: 'error',
      message: 'FAILURE',
      type: status.FAILURE_STATUS,
    };
    state.list = [];
    state.listCount = 0;
    state.listOffset = 0;
  },
  [fetchStatusList.pending]: () => {},
  [fetchStatusList.fulfilled]: (state, { payload }) => {
    const statusList = payload && payload.length ? payload : [];
    state.statusList = statusList.map((statusOption, index) => ({
      id: index,
      label: capitalize(statusOption),
    }));
  },
  [fetchStatusList.rejected]: (state) => {
    state.statusList = [];
  },
  [changeStatus.pending]: (state) => {
    state.status = {
      severity: 'info',
      message: 'Updating status',
      type: status.LOADING_STATUS,
    };
  },
  [changeStatus.fulfilled]: (state, { meta }) => {
    const { arg } = meta;
    const { project, status: statusProject } = arg;
    const list = state && state.list && state.list.length ? state.list : [];
    state.list = list.map((p) =>
      p.id === project.id
        ? {
            ...p,
            status: statusProject.label.toLowerCase(),
          }
        : p
    );
    state.status = {
      severity: false,
      message: 'IDLE ',
      type: status.IDLE_STATUS,
    };
  },
  [changeStatus.rejected]: (state) => {
    state.status = {
      severity: 'error',
      message: 'FAILURE Updating status',
      type: status.FAILURE_STATUS,
    };
  },
};
export { fetchProjects, fetchStatusList, changeStatus };
